/**
 * reportSpanSelectors — the SINGLE `column-span: all` allowlist for the report
 * two-column engine (W2 / RC1).
 *
 * Wide content (tables, figures, charts, explicit span hooks) must break OUT of
 * the two-column flow and span BOTH columns so nothing is squashed to half
 * width — this is the "no data loss" invariant: every wide block that renders in
 * one column also renders, full-width, in two columns.
 *
 * That allowlist was previously hand-duplicated across THREE column scopes:
 *   1. the on-screen per-section ISLAND scope  (SiteRagReportView `<style>`)
 *   2. the paged page-level FLOW scope         (SiteRagReportView `<style>`)
 *   3. the @media print / export scope          (index.css)
 * Three copies drift: a hook added to one (e.g. `[data-span-columns]`) silently
 * went missing in another, so a table that spanned on screen squashed in the
 * PDF. This module is the ONE source of the leaf list; the two JS-rendered
 * scopes build their selectors from {@link spanColumnsSelectors}, and a drift
 * test pins the static index.css copy to {@link SPAN_COLUMNS_LEAVES} so the
 * three can never silently diverge again.
 *
 * The map figure is intentionally EXCLUDED via `:not([data-keep-in-column])`
 * (it renders framed / as an exhibit per the site-map treatment and must stay
 * in-column); the same opt-out hook lets any future wide figure stay in-column.
 */

/**
 * The leaf selectors (relative to a scope prefix) whose elements span BOTH
 * columns. Order is stable so the generated selector strings are deterministic
 * (byte-stable across renders / snapshots).
 */
export const SPAN_COLUMNS_LEAVES: readonly string[] = [
  'table',
  'figure',
  '[data-figure]:not([data-keep-in-column])',
  '[data-span-columns]',
  // N1 (Editorial Report Redesign v2.0) — the `data-flow="span"` classification
  // contract. An editorial section marks every EXHIBIT block (table / chart /
  // KPI-rail / datasheet / matrix / map / timeline) with `data-flow="span"`;
  // those blocks `column-span: all` so they break OUT of the N2 two-column prose
  // flow and read full-width. Running PROSE carries `data-flow="flow"` instead
  // (never matched here → it columnises). This is the missing block-level
  // classification the prior 0/11 two-column attempt lacked. Additive: nothing
  // carries `data-flow="span"` until an editorial section emits it, so the
  // selector is inert on every legacy/non-editorial report (byte-stable).
  '[data-flow="span"]',
  '.recharts-wrapper',
] as const;

/**
 * Build the comma-joined `column-span: all` selector group for one column
 * scope. `prefix` is the scope ancestor chain (e.g.
 * `'[data-report-body-columns="2"] [data-section-columns="2"]:not([data-section="cover"])'`
 * for the island scope, or
 * `'[data-report-paged][data-page-flow="2"] [data-page-flow-box]'` for the flow
 * scope). Each leaf is prefixed and the results joined with `,\n` + `indent` so
 * the emitted CSS reads cleanly inside the `<style>` literal.
 *
 * @param prefix scope ancestor chain (no trailing space needed)
 * @param indent leading whitespace for each continuation line (cosmetic)
 */
export function spanColumnsSelectors(prefix: string, indent = '          '): string {
  return SPAN_COLUMNS_LEAVES.map((leaf) => `${prefix} ${leaf}`).join(`,\n${indent}`);
}
